<template>
  <UiDialog v-model="visible" :title="$t('加锁确认')" width="420px" custom-class="ground-station-lock-dialog" @close="close">
    <p class="lock-dialog-hint">{{ $t("{0} · 确认周围安全后，滑动以发送加锁指令", [(plane ? plane.name : '未选择飞机')]) }}</p>
    <div ref="lockSlideTrack" class="lock-slide-track">
      <span>{{ $t(lockSlideProgress >= 85 ? '松开确认加锁' : '按住滑块，向右滑动') }}</span>
      <div
        ref="lockSlideHandle"
        class="lock-slide-handle"
        :class="{ dragging: lockSlideDragging }"
        :style="{ transform: `translateX(${lockSlideOffset}px)` }"
        @pointerdown.stop.prevent="startLockSlide"
        @pointermove.stop.prevent="moveLockSlide"
        @pointerup.stop.prevent="endLockSlide"
        @pointercancel.stop.prevent="cancelLockSlide"
      ><i class="iconfont icon-suoding"></i></div>
    </div>
    <span slot="footer"><UiButton action="cancel" @click="close">{{ $t("取消") }}</UiButton></span>
  </UiDialog>
</template>

<script>
export default {
  name: 'GroundStationLockDialog',
  props: {
    value: { type: Boolean, default: false },
    plane: { type: Object, default: null },
    disabled: { type: Boolean, default: false }
  },
  data () {
    return {
      lockSlideDragging: false,
      lockSlideOffset: 0,
      lockSlideProgress: 0,
      lockSlideStartX: 0,
      lockSlideStartOffset: 0,
      lockSlideMaxOffset: 0,
      lockSliderTimer: null
    }
  },
  computed: {
    visible: {
      get () { return this.value },
      set (value) { this.$emit('input', value) }
    },
    canLock () {
      const state = (this.plane && this.plane.planeState) || {}
      return Boolean(this.plane) && !this.disabled && ![true, 1, '1'].includes(state.isAutoGoodsDown)
    }
  },
  watch: {
    value (value) {
      this.clearLockSliderTimer()
      this.resetLockSlide()
      if (value && this.canLock) this.armLockSliderTimer()
      else if (value) this.close()
    },
    plane () { this.close() },
    canLock (value) { if (!value) this.close() }
  },
  beforeDestroy () { this.clearLockSliderTimer() },
  methods: {
    close () {
      this.clearLockSliderTimer()
      this.resetLockSlide()
      this.visible = false
    },
    clearLockSliderTimer () {
      if (!this.lockSliderTimer) return
      clearTimeout(this.lockSliderTimer)
      this.lockSliderTimer = null
    },
    armLockSliderTimer () {
      this.clearLockSliderTimer()
      this.lockSliderTimer = setTimeout(() => this.close(), 10000)
    },
    resetLockSlide () {
      this.lockSlideDragging = false
      this.lockSlideOffset = 0
      this.lockSlideProgress = 0
      this.lockSlideStartX = 0
      this.lockSlideStartOffset = 0
      this.lockSlideMaxOffset = 0
    },
    startLockSlide (event) {
      if (!this.value || !this.canLock) return
      const track = this.$refs.lockSlideTrack
      const handle = this.$refs.lockSlideHandle
      if (!track || !handle) return
      this.lockSlideMaxOffset = Math.max(0, track.clientWidth - handle.clientWidth - 4)
      this.lockSlideStartX = event.clientX
      this.lockSlideStartOffset = this.lockSlideOffset
      this.lockSlideDragging = true
      this.clearLockSliderTimer()
      if (event.currentTarget.setPointerCapture) event.currentTarget.setPointerCapture(event.pointerId)
    },
    moveLockSlide (event) {
      if (!this.lockSlideDragging) return
      const offset = this.lockSlideStartOffset + event.clientX - this.lockSlideStartX
      this.lockSlideOffset = Math.min(this.lockSlideMaxOffset, Math.max(0, offset))
      this.lockSlideProgress = this.lockSlideMaxOffset > 0 ? Math.round(this.lockSlideOffset / this.lockSlideMaxOffset * 100) : 0
    },
    endLockSlide () {
      if (!this.lockSlideDragging) return
      this.lockSlideDragging = false
      if (this.value && this.canLock && this.lockSlideMaxOffset > 0 && this.lockSlideProgress >= 85) {
        this.$emit('confirm')
        this.close()
        return
      }
      this.lockSlideOffset = 0
      this.lockSlideProgress = 0
      this.armLockSliderTimer()
    },
    cancelLockSlide () {
      if (!this.lockSlideDragging) return
      this.lockSlideDragging = false
      this.lockSlideOffset = 0
      this.lockSlideProgress = 0
      this.armLockSliderTimer()
    }
  }
}
</script>

<style lang="scss" scoped>
.lock-dialog-hint { margin: 0 0 var(--ui-space-lg); color: var(--ui-text-secondary); font-size: var(--ui-font-size-body); line-height: 1.5; }
.lock-slide-track { height: 64px; position: relative; display: flex; align-items: center; justify-content: center; overflow: hidden; padding-left: 64px; color: var(--ui-text-secondary); border: 1px solid rgba(var(--ui-color-danger-emphasis-rgb), .35); border-radius: 8px; background: var(--ui-bg-control); font-size: var(--ui-font-size-body); user-select: none; box-sizing: border-box; }
.lock-slide-handle { width: 56px; height: 56px; position: absolute; left: 4px; top: 3px; display: grid; place-items: center; color: var(--ui-color-white); border-radius: 6px; background: var(--ui-color-danger-active); cursor: grab; touch-action: none; box-shadow: 0 2px 8px rgba(var(--ui-color-danger-emphasis-rgb), .2); }
.lock-slide-handle.dragging { cursor: grabbing; }
</style>

<style lang="scss">
.ground-station-lock-dialog.el-dialog { position: fixed; top: 50%; left: 50%; width: min(420px, calc(100vw - 32px)) !important; margin: 0 !important; transform: translate(-50%, -50%); }
.ui-dialog-wrapper.dialog-fade-enter-active > .ui-dialog.ground-station-lock-dialog:not(.is-fullscreen) {
  animation: ground-station-lock-dialog-in var(--ui-duration-normal) var(--ui-ease-standard);
}
.ui-dialog-wrapper.dialog-fade-leave-active > .ui-dialog.ground-station-lock-dialog:not(.is-fullscreen) {
  animation: ground-station-lock-dialog-in var(--ui-duration-fast) ease reverse;
}
@keyframes ground-station-lock-dialog-in {
  from { transform: translate(-50%, -50%) scale(.98); }
  to { transform: translate(-50%, -50%) scale(1); }
}
</style>
